import { useQuery } from "@-/com.npmjs.tanstack__react-query";
import { Link } from "@-/com.npmjs.expo-router";
import { ActivityIndicator, Pressable, View } from "@-/com.npmjs.react-native";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { findContainingIdioms } from "@-/dev.improve.haniverse.data.idioms";
import { useContainingWordsQueryOptions } from "@-/dev.improve.haniverse.lib.morpheme.word.read";
import { useActorUser } from "@-/dev.improve.haniverse.lib.user";
import { useRouteBodyState } from "@-/dev.improve.haniverse.lib.navigation.route-body-state";
import { WordRow } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._components.panel-content._components.word-row";
import { useMemo } from "react";

const PAGE_SIZE = 10;

/** Longer expressions containing the whole word, with chengyu identified by the shared lexicon. */
export function WordDictionaryOccurrences({ word }: { word: string }) {
  const { hanziScript } = useActorUser();
  const query = useQuery(useContainingWordsQueryOptions(word));
  const chengyu = useMemo(() => findContainingIdioms(word), [word]);
  const chengyuHeadwords = useMemo(
    () => new Set(chengyu.flatMap((entry) => [entry.simplified, entry.traditional])),
    [chengyu],
  );
  const dictionaryByWord = useMemo(
    () => new Map(query.data?.map((entry) => [entry.word, entry])),
    [query.data],
  );
  const otherWords = useMemo(
    () => query.data?.filter((entry) => !chengyuHeadwords.has(entry.word)) ?? [],
    [chengyuHeadwords, query.data],
  );
  const [chengyuCount, setChengyuCount] = useRouteBodyState(
    "word-occurrences-chengyu-count",
    PAGE_SIZE,
  );
  const [wordCount, setWordCount] = useRouteBodyState("word-occurrences-other-count", PAGE_SIZE);

  return (
    <View className="gap-y-3 p-3" testID="word-dictionary-occurrences">
      {query.isLoading ? (
        <ActivityIndicator accessibilityLabel="Loading word occurrences" size="small" />
      ) : null}
      <Text className="text-xs text-muted-foreground">Expressions containing {word}.</Text>
      <View className="gap-y-2" testID="word-occurrences-chengyu">
        <Text accessibilityRole="header" className="text-xs font-semibold text-muted-foreground">
          Chengyu
        </Text>
        {chengyu.length === 0 ? (
          <Text className="text-sm text-muted-foreground">No chengyu containing {word} found.</Text>
        ) : (
          chengyu.slice(0, chengyuCount).map((idiom) => {
            const entry =
              dictionaryByWord.get(idiom.simplified) ?? dictionaryByWord.get(idiom.traditional);
            const display = hanziScript === "traditional" ? idiom.traditional : idiom.simplified;
            return entry ? (
              <WordRow key={idiom.simplified} word={entry} />
            ) : (
              <Link
                asChild
                href={`https://www.mdbg.net/chinese/dictionary?page=worddict&wdqb=${encodeURIComponent(display)}`}
                key={idiom.simplified}
                target="_blank"
              >
                <Pressable
                  accessibilityLabel={`Look up ${display} in MDBG`}
                  accessibilityRole="link"
                  className="rounded-xs border border-border p-3 active:bg-accent"
                >
                  <Text className="text-xl text-foreground">{display}</Text>
                  <Text className="text-xs text-primary">Look up pronunciation and meaning</Text>
                </Pressable>
              </Link>
            );
          })
        )}
        {chengyuCount < chengyu.length ? (
          <Pressable
            accessibilityRole="button"
            className="p-2"
            onPress={() => setChengyuCount((count) => count + PAGE_SIZE)}
          >
            <Text className="text-center text-primary">Show more chengyu</Text>
          </Pressable>
        ) : null}
      </View>
      <View className="gap-y-2" testID="word-occurrences-other-words">
        <Text accessibilityRole="header" className="text-xs font-semibold text-muted-foreground">
          Other words
        </Text>
        {query.error ? (
          <View className="gap-y-2">
            <Text className="text-sm text-destructive">Failed to load other words.</Text>
            <Pressable
              accessibilityRole="button"
              onPress={() => {
                void query.refetch();
              }}
            >
              <Text className="text-primary">Retry word occurrences</Text>
            </Pressable>
          </View>
        ) : query.isLoading ? null : otherWords.length === 0 ? (
          <Text className="text-sm text-muted-foreground">
            No other words containing {word} found.
          </Text>
        ) : (
          otherWords.slice(0, wordCount).map((entry) => <WordRow key={entry.id} word={entry} />)
        )}
        {wordCount < otherWords.length ? (
          <Pressable
            accessibilityRole="button"
            className="p-2"
            onPress={() => setWordCount((count) => count + PAGE_SIZE)}
          >
            <Text className="text-center text-primary">Show more other words</Text>
          </Pressable>
        ) : null}
      </View>
    </View>
  );
}
